import { useCallback, useEffect } from 'react'

import { useAnalyticsTrack, useAnalyticsView } from '@/features/amplitude'
import { Banner, BannerVariants, Icon, Text, Progress } from '@/ui'
import { TypographyStyles, TextColors, mtc } from '@/styles'
import {
  faCheckCircle,
  faCheck,
  faCommentQuestion,
  faShieldUser,
} from '@fortawesome/pro-solid-svg-icons'
import { ReviewTransactionsIcon } from './ReviewTransactionsIcon'
import { AutoCatIcon } from './AutoCatIcon'
import { useTransactionReviewAnimation } from './useTransactionReviewAnimation'
import { OnboardingStickyNav } from '../shared/OnboardingStickyNav'
import { useFixedFooterOffset } from '../shared/useFixedFooterOffset'
import { ONBOARDING_SCREEN } from '../shared/onboardingScreen.styles'
import { SeparationLoadError } from '../shared/SeparationLoadError'
import { plural, transactionNoun } from '../shared/plural'
import { SeparationSkipScreen, useSeparationQuestions } from '../separation'

interface TransactionReviewProps {
  onBack?: () => void
  onContinue?: () => void
  onSkip: (screen: SeparationSkipScreen, openQuestions: number) => void
}

const ChecklistItem = ({
  label,
  isComplete,
}: {
  label: string
  isComplete: boolean
}) => (
  <div className="flex items-center gap-2">
    <Icon
      icon={faCheckCircle}
      color={isComplete ? 'text-green-50' : 'text-neutral-100'}
      className="transition-colors duration-500"
      size="1x"
      backgroundClassName="h-4 w-4 shrink-0"
    />
    <Text
      variant={TypographyStyles.BODY_SM}
      className={mtc(
        'transition-colors duration-500',
        isComplete ? 'text-neutral-600' : 'text-neutral-400'
      )}
    >
      {label}
    </Text>
  </div>
)

const TransactionCount = ({ count }: { count: number }) => (
  <strong>
    {count.toLocaleString()} {transactionNoun(count)}
  </strong>
)

/** The follow-up step's body. Split out because it reads two ways: every unresolved
 * transaction is covered by a question, or only some of them are. */
/**
 * "we need your input on", not "we're unsure about": these questions cover money movement
 * too, and the step above tells the member transfers are settled and out of the sort. The
 * uncertainty wording belongs to the `unclear` step, which excludes them.
 */
const FollowUpBody = ({
  questionCount,
  covered,
  unresolved,
}: {
  questionCount: number
  covered: number
  unresolved: number
}) => {
  const verb = plural(questionCount, 'question covers', 'questions cover')

  if (covered < unresolved) {
    return (
      <>
        <strong>{questionCount}</strong> quick {verb}{' '}
        <strong>{covered.toLocaleString()}</strong> of the{' '}
        <TransactionCount count={unresolved} /> we need your input on. Your
        bookkeeping team will handle the rest.
      </>
    )
  }

  if (unresolved === 1) {
    return (
      <>
        Just <strong>1</strong> quick question about the{' '}
        <TransactionCount count={unresolved} /> we need your input on.
      </>
    )
  }

  return (
    <>
      Just <strong>{questionCount}</strong> quick {verb} all{' '}
      <TransactionCount count={unresolved} /> we need your input on.
    </>
  )
}

interface FoundCounts {
  business: number
  personal: number
  unresolved: number
  questionCount: number
  covered: number
  movement: number
  likelyBusiness: number
  likelyPersonal: number
  leftForBookkeeper: number
  unclear: number
}

/**
 * Every step the card can show, each declaring when it applies. A step with nothing behind
 * it is dropped rather than rendered as a zero.
 */
const foundStepsFor = ({
  business,
  personal,
  unresolved,
  questionCount,
  covered,
  movement,
  likelyBusiness,
  likelyPersonal,
  leftForBookkeeper,
  unclear,
}: FoundCounts) => [
  {
    when: business > 0,
    id: 'business',
    iconClassName: 'bg-green-50',
    icon: faCheck,
    title: `${business.toLocaleString()} business ${transactionNoun(business)}`,
    description: plural(
      business,
      'Our bookkeeping team will categorize this for you.',
      'Our bookkeeping team will categorize these for you.'
    ),
  },
  {
    when: personal > 0,
    id: 'personal',
    iconClassName: 'bg-green-50',
    icon: faCheck,
    title: `${personal.toLocaleString()} personal ${transactionNoun(personal)}`,
    description: plural(
      personal,
      'This looks like a personal expense, not a business one. Your bookkeeping team will confirm.',
      'These look like personal expenses, not business ones. Your bookkeeping team will confirm.'
    ),
  },
  {
    when: likelyBusiness > 0,
    id: 'likely-business',
    iconClassName: 'bg-yellow-100',
    icon: faCommentQuestion,
    title: `${likelyBusiness.toLocaleString()} likely business`,
    description: (
      <>
        Other practices almost always treat these as business expenses. Confirm
        them in the questions we&apos;ll ask in the next step and we&apos;ll
        apply it.
      </>
    ),
  },
  {
    when: likelyPersonal > 0,
    id: 'likely-personal',
    iconClassName: 'bg-yellow-100',
    icon: faCommentQuestion,
    title: `${likelyPersonal.toLocaleString()} likely personal`,
    description: (
      <>
        These look like personal spending based on how other practices treat
        them. Confirm in the next step to keep them out of your books.
      </>
    ),
  },
  {
    when: movement > 0,
    id: 'transfers',
    iconClassName: 'bg-green-50',
    icon: faCheck,
    title: `${movement.toLocaleString()} ${plural(movement, 'transfer or card payment', 'transfers & card payments')}`,
    description: (
      <>
        Money moving between your own accounts, plus payroll and card payments.
        These aren&apos;t expenses, so we keep them out of the
        business-vs-personal sort.
      </>
    ),
  },
  {
    when: leftForBookkeeper > 0,
    id: 'left-for-bookkeeper',
    iconClassName: 'bg-green-50',
    icon: faCheck,
    title: `${leftForBookkeeper.toLocaleString()} left to your bookkeeping team`,
    description: (
      <>
        You told us these accounts are all business or all personal, so we
        won&apos;t ask about {plural(leftForBookkeeper, 'this one', 'these')}.
        Your bookkeeping team will review{' '}
        {plural(leftForBookkeeper, 'it', 'them')}.
      </>
    ),
  },
  {
    when: unclear > 0,
    id: 'unclear',
    iconClassName: 'bg-yellow-100',
    icon: faCommentQuestion,
    title: `${unclear.toLocaleString()} we're not sure about`,
    description: (
      <>
        We have no signal on these either way, so we&apos;ll ask you about them
        in the next step, grouped by merchant so it&apos;s a handful of
        questions rather than one per transaction. Not sure about one? Mark it
        and your bookkeeping team will take it from there.
      </>
    ),
  },
  {
    when: questionCount > 0,
    id: 'follow-up',
    iconClassName: 'bg-yellow-100',
    icon: faCommentQuestion,
    title: plural(
      questionCount,
      'One follow-up question',
      'A few follow-up questions'
    ),
    description: (
      <FollowUpBody
        questionCount={questionCount}
        covered={covered}
        unresolved={unresolved}
      />
    ),
  },
]

export const buildFoundSteps = (counts: FoundCounts) =>
  foundStepsFor(counts).filter((step) => step.when)

const headlineFor = ({
  sortedNothing,
  emptyBatch,
}: {
  sortedNothing: boolean
  emptyBatch: boolean
}): string => {
  if (emptyBatch) return 'Your books are already sorted'
  return sortedNothing ? 'Transactions reviewed' : 'Transactions sorted'
}

/** Reads three ways: still importing, nothing was auto-sorted, or a real denominator. */
const counterCaption = ({
  total,
  sortedNothing,
}: {
  total: number
  sortedNothing: boolean
}): string => {
  if (total === 0) return 'transactions so far'
  if (sortedNothing) return 'transactions reviewed and grouped'
  return `of ${total.toLocaleString()} transactions`
}

const FoundStep = ({
  iconClassName,
  icon,
  title,
  description,
}: ReturnType<typeof buildFoundSteps>[number]) => (
  <div className="flex w-full flex-col py-2">
    <div className="flex w-full items-center gap-4">
      <div
        className={mtc(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
          iconClassName
        )}
      >
        <Icon icon={icon} color="text-neutral-0" size="1x" />
      </div>
      <Text variant={TypographyStyles.LABEL_MD} color={TextColors.HEADER}>
        {title}
      </Text>
    </div>
    <div className="pr-6 pl-12">
      <Text variant={TypographyStyles.BODY_SM} color={TextColors.EMPHASIS}>
        {description}
      </Text>
    </div>
  </div>
)

export const TransactionReview = ({
  onBack,
  onContinue,
  onSkip,
}: TransactionReviewProps) => {
  const { counts, questions, status, loadFailed, refetch } =
    useSeparationQuestions()
  const settled =
    counts.business + counts.personal + counts.setAside + counts.unsure
  // Total for this pass: everything settled plus everything still needing an answer. Zero
  // until the questions payload lands, which is what keeps the bar indeterminate early on.
  const total = settled + counts.unresolved
  const isSettled = status === 'ready'
  // With nothing auto-sorted there is no number worth counting to, and a big "0" reads as
  // failure. Fall back to what we did do: review every transaction and group it.
  const sortedNothing = isSettled && settled === 0
  // A member whose rows a bookkeeper already confirmed has nothing in any bucket, so the
  // fallback below lands on 0 too. A big 0 under "reviewed", with the importing caption and
  // an indeterminate bar, reads as a hang rather than as finished work.
  const emptyBatch = isSettled && total === 0
  const headline = sortedNothing ? total : settled
  const foundSteps = buildFoundSteps({
    business: counts.business,
    personal: counts.personal,
    unresolved: counts.unresolved,
    questionCount: questions?.length ?? 0,
    covered: (questions ?? []).reduce((sum, q) => sum + q.subItems.length, 0),
    movement: counts.setAside + counts.movementPending,
    likelyBusiness: counts.likelyBusiness,
    likelyPersonal: counts.likelyPersonal,
    leftForBookkeeper: counts.leftForBookkeeper,
    unclear: counts.unclear,
  })
  const track = useAnalyticsTrack()
  const pageView = useAnalyticsView()
  const leftOffset = useFixedFooterOffset()
  const {
    magnifyingGlassRef,
    ellipsisRef,
    foundStepsRef,
    sparkleOneRef,
    sparkleTwoRef,
    count,
    isComplete,
    progressPercent,
  } = useTransactionReviewAnimation({ settled: headline, total, isSettled })

  useEffect(() => {
    pageView('onboarding transaction review')
  }, [pageView])

  const handleBack = useCallback(() => {
    track('onboarding_transaction_review_back')
    onBack?.()
  }, [track, onBack])

  const handleContinue = useCallback(() => {
    track('onboarding_transaction_review_continue')
    if (isComplete) {
      onContinue?.()
    }
  }, [track, isComplete, onContinue])

  const openQuestions = questions?.length ?? 0
  const skipFrom = (screen: SeparationSkipScreen) => () => {
    track('onboarding_transaction_review_skip', { screen, openQuestions })
    onSkip(screen, openQuestions)
  }

  if (loadFailed) {
    return (
      <SeparationLoadError
        onRetry={() => void refetch()}
        onBack={onBack}
        onSkip={skipFrom(SeparationSkipScreen.LoadError)}
      />
    )
  }

  return (
    <div className={ONBOARDING_SCREEN.page}>
      <div className={ONBOARDING_SCREEN.content}>
        <div className={ONBOARDING_SCREEN.iconWrapper}>
          {isComplete ? (
            <AutoCatIcon
              sparkleOneRef={sparkleOneRef}
              sparkleTwoRef={sparkleTwoRef}
              className={ONBOARDING_SCREEN.iconImage}
            />
          ) : (
            <ReviewTransactionsIcon
              magnifyingGlassRef={magnifyingGlassRef}
              className={ONBOARDING_SCREEN.iconImage}
            />
          )}
        </div>

        <div className={mtc(ONBOARDING_SCREEN.header, 'gap-6')}>
          <Text variant={TypographyStyles.DISPLAY_SM} textAlign="center">
            {isComplete ? (
              headlineFor({ sortedNothing, emptyBatch })
            ) : (
              <>
                Sorting your transactions
                <span
                  ref={ellipsisRef}
                  className="inline-block w-6 text-left"
                />
              </>
            )}
          </Text>
        </div>

        {!emptyBatch && (
          <div className="flex w-full flex-col items-center gap-6">
            <div className="flex flex-col items-center gap-0 text-center whitespace-nowrap">
              <Text variant={TypographyStyles.DISPLAY_LG}>
                {count.toLocaleString()}
              </Text>
              <Text
                variant={TypographyStyles.LABEL_SM}
                color={TextColors.MUTED}
              >
                {counterCaption({ total, sortedNothing })}
              </Text>
            </div>
            <Progress
              data-indeterminate={progressPercent === null ? 'true' : undefined}
              size="default"
              value={progressPercent ?? 0}
              className={mtc(
                ONBOARDING_SCREEN.contentColumn,
                'w-150 max-w-full'
              )}
            />
          </div>
        )}

        {isComplete ? (
          <div
            className={mtc(
              ONBOARDING_SCREEN.surfaceCard,
              ONBOARDING_SCREEN.narrowColumn,
              'gap-6'
            )}
          >
            <Text variant={TypographyStyles.H2} color={TextColors.HEADER}>
              What we found
            </Text>
            <div className="flex w-full flex-col" ref={foundStepsRef}>
              {foundSteps.length > 0 ? (
                foundSteps.map((step) => <FoundStep key={step.id} {...step} />)
              ) : (
                <Text
                  variant={TypographyStyles.BODY_SM}
                  color={TextColors.EMPHASIS}
                >
                  Nothing needed sorting in this batch. Your bookkeeping team
                  picks it up from here.
                </Text>
              )}
            </div>
            <Banner
              variant={BannerVariants.DEFAULT}
              icon={faShieldUser}
              wrapperClassName="w-full justify-start"
            >
              Our bookkeepers will review everything to double-check accuracy.
            </Banner>
          </div>
        ) : (
          <div className="flex w-full justify-center">
            <div className="flex flex-col items-start gap-2">
              <ChecklistItem
                label="Tallying up your income"
                isComplete={total > 0 && count >= total * 0.15}
              />
              <ChecklistItem
                label="Categorizing your expenses"
                isComplete={total > 0 && count >= total * 0.4}
              />
              <ChecklistItem
                label="Identifying personal transactions"
                isComplete={total > 0 && count >= total * 0.6}
              />
              <ChecklistItem
                label="Finding tax deductions"
                isComplete={total > 0 && count >= total * 0.8}
              />
              <ChecklistItem
                label="Flagging unclear transactions"
                isComplete={isComplete}
              />
            </div>
          </div>
        )}
      </div>

      <OnboardingStickyNav
        leftOffset={leftOffset}
        backIcon={null}
        continueIcon={null}
        className={ONBOARDING_SCREEN.footer}
        onBack={handleBack}
        onContinue={handleContinue}
        continueDisabled={!isComplete}
        onSkip={skipFrom(SeparationSkipScreen.TransactionReview)}
        skipLabel="Skip"
      />
    </div>
  )
}

export default TransactionReview
